import React, { useState } from "react";
import { useHistory } from "react-router-dom";

// Components
import Header from "./components/Header";

// Modal Component
const Modal = ({ isOpen, onClose, title, message, type = "info" }) => {
  if (!isOpen) return null;

  const getIconColor = () => {
    switch (type) {
      case "success": return "text-green-500";
      case "error": return "text-red-500";
      default: return "text-blue-500";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        );
      case "error":
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className={`${getIconColor()} mr-3`}>
            {getIcon()}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 font-raleway">
            {title}
          </h3>
        </div>
        <p className="text-gray-600 mb-6 font-raleway">
          {message}
        </p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-[#85C4FF] text-black px-6 py-2 rounded-lg font-raleway font-medium hover:bg-[#75B4EF] transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CompanyForm2025() {
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });

  const [formData, setFormData] = useState({
    // Step 1 fields - matching backend field names
    empresa: "",
    nome: "",
    email: "",
    cargo: "",
    telefone: "",
    privacidade: false,
    
    // Step 2 fields - matching backend field names  
    quantidade_colaboradores: "",
    objetivo: "", // This will store selected interests as comma-separated string
    mensagem: "", // For "other" specification
  });

  const showModal = (title, message, type = "info") => {
    setModal({ isOpen: true, title, message, type });
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: "", message: "", type: "info" });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRadioChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name, value, checked) => {
    setFormData(prev => {
      const currentValues = prev[name] ? prev[name].split(', ') : [];
      let newValues;
      
      if (checked) {
        newValues = [...currentValues, value];
      } else {
        newValues = currentValues.filter(v => v !== value);
      }
      
      return {
        ...prev,
        [name]: newValues.join(', ')
      };
    });
  };

  const validateStep1 = () => {
    const { empresa, nome, email, cargo, privacidade } = formData;
    
    if (!empresa.trim()) {
      showModal("Campo obrigatório", "Por favor, preencha o nome da empresa.", "error");
      return false;
    }
    
    if (!nome.trim()) {
      showModal("Campo obrigatório", "Por favor, preencha o nome da pessoa responsável.", "error");
      return false;
    }
    
    if (!email.trim()) {
      showModal("Campo obrigatório", "Por favor, preencha o e-mail corporativo.", "error");
      return false;
    }
    
    if (!email.includes("@") || !email.includes(".")) {
      showModal("Email inválido", "Por favor, insira um email válido.", "error");
      return false;
    }
    
    if (!cargo.trim()) {
      showModal("Campo obrigatório", "Por favor, preencha o cargo ou área de atuação.", "error");
      return false;
    }
    
    if (!privacidade) {
      showModal("Termos obrigatórios", "Por favor, aceite os Termos de Uso para continuar.", "error");
      return false;
    }
    
    return true;
  };

  const validateStep2 = () => {
    const { quantidade_colaboradores, objetivo } = formData;
    
    if (!quantidade_colaboradores) {
      showModal("Campo obrigatório", "Por favor, selecione o porte da empresa.", "error");
      return false;
    }
    
    if (!objetivo.trim()) {
      showModal("Campo obrigatório", "Por favor, selecione pelo menos um assunto de interesse.", "error");
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/api/contato`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          // Add default values for backend compatibility
          contato: formData.telefone || "",
          organizacao: "Formulário Empresarial 2025",
        }),
      });

      if (response.ok) {
        showModal("Sucesso!", "Formulário enviado com sucesso! Entraremos em contato em breve.", "success");
        // Reset form after success
        setTimeout(() => {
          history.push("/entrance");
        }, 2000);
      } else {
        showModal("Erro", "Erro ao enviar formulário. Tente novamente.", "error");
      }
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      showModal("Erro", "Erro ao enviar formulário. Verifique sua conexão e tente novamente.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#FAFAFA] font-raleway">
        <Header />

        {/* Form Content */}
        <div className="max-w-[456px] mx-auto px-4 sm:px-6 md:px-8 pb-12 md:pb-16 lg:pb-20 xl:pb-24">
          {/* Progress Bar */}
          <div className="flex items-center justify-center gap-2 mb-5 md:mb-8 lg:mb-10 xl:mb-[20px]">
            <div className={`w-3 h-3 rounded-full ${currentStep >= 1 ? 'bg-[#85C4FF]' : 'bg-[#888888]'}`}></div>
            <div className="w-10 h-0 border-t border-[#85C4FF]"></div>
            <div className={`w-3 h-3 rounded-full ${currentStep >= 2 ? 'bg-[#85C4FF]' : 'bg-white border border-[#888888]'}`}></div>
          </div>

          {/* Step 1 */}
          {currentStep === 1 && (
            <div className="space-y-5 md:space-y-6 lg:space-y-8 xl:space-y-[20px]">
              {/* Title */}
              <div className="text-center space-y-3 md:space-y-4 lg:space-y-5 xl:space-y-[16px]">
                <h1 className="font-raleway font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[28px] leading-tight text-[#002342]">
                  Quer levar impacto real para sua marca?
                </h1>
                <p className="font-raleway text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[18px] leading-relaxed text-[#121212] max-w-[400px] mx-auto">
                  Preencha em poucos passos o formulário abaixo e entraremos em contato com uma proposta personalizada. Será um prazer cocriar soluções que unam propósito, engajamento e resultados.
                </p>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-center gap-2 md:gap-3 lg:gap-4 xl:gap-[8px]">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md">
                  <input
                    type="checkbox"
                    name="privacidade"
                    checked={formData.privacidade}
                    onChange={handleInputChange}
                    className="w-6 h-6 text-[#1AABFF] border-gray-300 rounded focus:ring-[#1AABFF] focus:ring-2"
                  />
                </div>
                <div className="text-sm md:text-base lg:text-lg xl:text-[16px] text-[#121212]">
                  <span className="font-roboto">Eu concordo com os </span>
                  <a
                    onClick={() => history.push('/term-of-use')}
                    className="font-roboto font-medium text-[#1AABFF] hover:underline cursor-pointer"
                  >
                    Termos de Uso.
                  </a>
                </div>
              </div>

              {/* Form Fields */}
              <div className="border border-[#E7E7E7] rounded-lg p-5 md:p-6 lg:p-7 xl:p-[32px] space-y-5 md:space-y-6 lg:space-y-7 xl:space-y-[20px]">
                <h2 className="font-raleway font-bold text-lg md:text-xl lg:text-2xl xl:text-[20px] text-[#002342]">
                  Cadastre sua empresa
                </h2>

                {/* Nome da empresa */}
                <div className="space-y-2">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Nome da empresa*
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 13 11" fill="none">
                        <path d="M1 1h11v9H1V1z" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                        <path d="M1 3l5.5 4L12 3" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleInputChange}
                        placeholder="Digite o nome da sua organização"
                        className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888] bg-transparent outline-none placeholder-[#888888]"
                      />
                    </div>
                  </div>
                </div>

                {/* Nome da pessoa responsável */}
                <div className="space-y-2">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Nome da pessoa responsável*
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 10 9" fill="none">
                        <path d="M5 4.5a2 2 0 100-4 2 2 0 000 4zM1 8.5a4 4 0 018 0" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        placeholder="Seu nome completo"
                        className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888] bg-transparent outline-none placeholder-[#888888]"
                      />
                    </div>
                  </div>
                </div>

                {/* E-mail corporativo */}
                <div className="space-y-2">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    E-mail corporativo*
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 10 8" fill="none">
                        <path d="M1 1h8v6H1V1z" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                        <path d="M1 2l4 3 4-3" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="nome@empresa.com"
                        className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888] bg-transparent outline-none placeholder-[#888888]"
                      />
                    </div>
                  </div>
                </div>

                {/* Cargo ou área de atuação */}
                <div className="space-y-2">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Cargo ou área de atuação*
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 10 10" fill="none">
                        <rect x="1" y="1" width="8" height="8" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                        <path d="M3 4h4M3 6h4" stroke="currentColor" strokeWidth="0.7"/>
                      </svg>
                      <input
                        type="text"
                        name="cargo"
                        value={formData.cargo}
                        onChange={handleInputChange}
                        placeholder="Ex: Marketing, ESG, RH, Inovação, Diretoria"
                        className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888] bg-transparent outline-none placeholder-[#888888]"
                      />
                    </div>
                  </div>
                </div>

                {/* Telefone ou WhatsApp */}
                <div className="space-y-2">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Telefone ou WhatsApp (opcional)
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 10 10" fill="none">
                        <path d="M2 1h6v8H2V1z" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                        <circle cx="5" cy="3" r="1" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleInputChange}
                        placeholder="DDD + número"
                        className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888] bg-transparent outline-none placeholder-[#888888]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <div className="flex justify-end mt-6 md:mt-8 lg:mt-10 xl:mt-12">
                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className="bg-[#002342] text-white px-6 py-2 rounded-[20px] font-raleway font-semibold text-sm md:text-base lg:text-lg xl:text-[16px] hover:bg-[#85C4FF] transition-colors flex items-center gap-3 disabled:opacity-50"
                >
                  <span>Próximo</span>
                  <svg className="w-4 h-3" viewBox="0 0 16 12" fill="none">
                    <path d="M10 1l5 5-5 5M15 6H1" stroke="#C684F5" strokeWidth="2" fill="none"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <div className="space-y-5 md:space-y-6 lg:space-y-8 xl:space-y-[20px]">
              {/* Title */}
              <div className="text-center space-y-3 md:space-y-4 lg:space-y-5 xl:space-y-[16px]">
                <h1 className="font-raleway font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[28px] leading-tight text-[#002342]">
                  Finalize seu cadastro
                </h1>
                <p className="font-raleway text-base sm:text-lg md:text-xl lg:text-2xl xl:text-[18px] leading-relaxed text-[#121212] max-w-[400px] mx-auto">
                  Preencha em poucos passos o formulário abaixo e entraremos em contato com uma proposta personalizada. Será um prazer cocriar soluções que unam propósito, engajamento e resultados.
                </p>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-center gap-2 md:gap-3 lg:gap-4 xl:gap-[8px]">
                <div className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md">
                  <input
                    type="checkbox"
                    name="privacidade"
                    checked={formData.privacidade}
                    onChange={handleInputChange}
                    className="w-6 h-6 text-[#1AABFF] border-gray-300 rounded focus:ring-[#1AABFF] focus:ring-2"
                  />
                </div>
                <div className="text-sm md:text-base lg:text-lg xl:text-[16px] text-[#121212]">
                  <span className="font-roboto">Eu concordo com os </span>
                  <a
                    onClick={() => history.push('/term-of-use')}
                    className="font-roboto font-medium text-[#1AABFF] hover:underline cursor-pointer"
                  >
                    Termos de Uso.
                  </a>
                </div>
              </div>

              {/* Form Fields */}
              <div className="border border-[#E7E7E7] rounded-lg p-5 md:p-6 lg:p-7 xl:p-[32px] space-y-5 md:space-y-6 lg:space-y-7 xl:space-y-[20px]">

                {/* Porte da empresa */}
                <div className="space-y-4">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Porte da empresa
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 13 11" fill="none">
                        <path d="M1 1h11v9H1V1z" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <span className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888]">
                        Selecione uma opção*
                      </span>
                    </div>
                  </div>

                  {/* Radio Options */}
                  <div className="space-y-3 pl-4">
                    {[
                      { value: "1_9", label: "Microempresa (até 9 colaboradores)" },
                      { value: "10_49", label: "Pequena empresa (10 a 49 colaboradores)" },
                      { value: "50_249", label: "Média empresa (50 a 249 colaboradores)" },
                      { value: "250_plus", label: "Grande empresa (250+ colaboradores)" }
                    ].map((option) => (
                      <div key={option.value} className="flex items-center gap-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="quantidade_colaboradores"
                            value={option.value}
                            checked={formData.quantidade_colaboradores === option.value}
                            onChange={(e) => handleRadioChange("quantidade_colaboradores", e.target.value)}
                            className="w-4 h-4 text-[#002342] border-[#E7E7E7] focus:ring-[#002342]"
                          />
                        </div>
                        <label className="font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#002342] cursor-pointer">
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assunto de interesse */}
                <div className="space-y-4">
                  <label className="font-raleway font-medium text-sm md:text-base lg:text-lg xl:text-[16px] text-[#002342]">
                    Assunto de interesse
                  </label>
                  <div className="relative">
                    <div className="flex items-center gap-4 px-4 py-2 border-b border-[#E7E7E7]">
                      <svg className="w-3 h-3 text-[#888888]" viewBox="0 0 11 11" fill="none">
                        <path d="M1 1h9v9H1V1z" stroke="currentColor" strokeWidth="0.7" fill="none"/>
                      </svg>
                      <span className="flex-1 font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#888888]">
                        Selecione uma ou mais opções*
                      </span>
                    </div>
                  </div>

                  {/* Checkbox Options */}
                  <div className="space-y-3 pl-4">
                    {[
                      { value: "Produto Solidário", label: "Produto Solidário (combo do bem, marketplace, API)" },
                      { value: "Experiências de Impacto", label: "Experiências de Impacto (eventos, ESG, engajamento)" },
                      { value: "Projetos Incentivados", label: "Projetos Incentivados (via leis de incentivo fiscal)" },
                      { value: "Parcerias institucionais", label: "Parcerias institucionais ou editais" },
                      { value: "Outro", label: "Outro (especifique abaixo)" }
                    ].map((option) => (
                      <div key={option.value} className="flex items-center gap-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.objetivo.includes(option.value)}
                            onChange={(e) => handleCheckboxChange("objetivo", option.value, e.target.checked)}
                            className="w-4 h-4 text-[#002342] border-[#E7E7E7] focus:ring-[#002342] rounded"
                          />
                        </div>
                        <label className="font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#002342] cursor-pointer">
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Other specification field */}
                  {formData.objetivo.includes("Outro") && (
                    <div className="mt-4">
                      <textarea
                        name="mensagem"
                        value={formData.mensagem}
                        onChange={handleInputChange}
                        placeholder="Especifique seu interesse..."
                        rows="3"
                        className="w-full px-4 py-2 border border-[#E7E7E7] rounded-lg font-raleway text-xs md:text-sm lg:text-base xl:text-[12px] text-[#002342] bg-transparent outline-none placeholder-[#888888] resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 md:mt-8 lg:mt-10 xl:mt-12">
                <button
                  onClick={handleBack}
                  className="bg-gray-200 text-[#002342] px-6 py-2 rounded-[20px] font-raleway font-semibold text-sm md:text-base lg:text-lg xl:text-[16px] hover:bg-gray-300 transition-colors"
                >
                  Voltar
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="bg-[#002342] text-white px-6 py-2 rounded-[20px] font-raleway font-semibold text-sm md:text-base lg:text-lg xl:text-[16px] hover:bg-[#85C4FF] transition-colors disabled:opacity-50"
                >
                  {isLoading ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </>
  );
}
